"use client";

import { Button } from "@/components/ui/Button";
import { Maximize, Menu, Minimize, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { setMobileNavOpen } from "../sidebar/mobile-nav";
import { NewMenu } from "./NewMenu";
import { NotificationsMenu } from "./NotificationsMenu";
import { SearchBox } from "./SearchBox";
import { SearchPalette } from "./SearchPalette";
import { UserMenu } from "./UserMenu";
import { useGlobalSearch } from "./useGlobalSearch";

export function Navbar(props: {
  userName: string;
  userRole: string | null;
  userSeed: number;
  userPhotoUrl?: string | null;
  userEmail?: string | null;
  canOpenAdminSettings: boolean;
  permissions: string[];
  clients: { id: number; name: string }[];
  applications: { id: number; name: string }[];
}) {
  const search = useGlobalSearch();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  }

  return (
    <>
      {/* No border-b: the navbar reads as a distinct region through its
          background color alone (matching the sidebar), not a bordered box.
          The content panel's own top edge (see layout.tsx) is the one
          divider. `relative z-header` keeps the navbar (and the search input
          inside it) above the search backdrop below, which otherwise would
          sit over the whole viewport including this bar.
          `grid-cols-[1fr_minmax(0,28rem)_1fr]` is what centers the search
          box exactly on the navbar's own midpoint regardless of how wide
          the icon cluster on the right ends up being — a plain flex/
          justify-between only centers when both sides happen to be equal
          width. The center track is `minmax(0, 28rem)`, not `auto` — an
          `auto` track sized against a percentage-width child (`w-full`)
          collapses to that child's min-content instead of the intended
          width, a real CSS Grid sizing gotcha. */}
      <header className="relative z-overlay grid h-16 shrink-0 grid-cols-[auto_1fr] items-center gap-3 bg-white px-2 sm:px-4 md:grid-cols-[1fr_minmax(0,28rem)_1fr] dark:bg-chrome">
        <div>
          <Button variant="ghost" iconOnly className="md:hidden" onClick={() => setMobileNavOpen(true)} aria-label="Open menu" title="Open menu">
            <Menu size={18} />
          </Button>
        </div>

        <SearchBox s={search} />

        <div className="flex items-center justify-self-end gap-0.5 sm:gap-1">
          <Button variant="ghost" iconOnly className="md:hidden" onClick={() => { search.setPaletteMode(true); search.setSearchOpen(true); }} aria-label="Search" title="Search">
            <Search size={18} />
          </Button>

          <NewMenu permissions={props.permissions} clients={props.clients} applications={props.applications} />

          <span className="hidden sm:contents">
          <Button variant="ghost" iconOnly onClick={toggleFullscreen} aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </Button>
          </span>

          <NotificationsMenu />

          <ThemeToggle />

          <UserMenu userName={props.userName} userRole={props.userRole} userSeed={props.userSeed} userPhotoUrl={props.userPhotoUrl} userEmail={props.userEmail} canOpenAdminSettings={props.canOpenAdminSettings} />
        </div>
      </header>

      <SearchPalette s={search} />
    </>
  );
}
